import { startOfDay } from "date-fns";
import type { DefaultValues } from "react-hook-form";
import { RecurrenceEnum } from "web_myplann/Infrastructure/Api/Api";
import { Resources } from "web_myplann/Infrastructure/Translations/Resources";
import type {
	Reminder,
	ResourceKey,
} from "web_myplann/Reminders/Types/ReminderTypes";
import { z } from "zod";

export const REMINDER_NAME_MAX_LENGTH = 50;

type TranslateFn = (key: ResourceKey, options?: unknown) => string;

export const getReminderFormSchema = (
	t: TranslateFn,
	initialDate: Date | null,
) =>
	z
		.object({
			typeKey: z.string().min(1),
			name: z
				.string({
					required_error: t(Resources.Reminders.Validations.nameRequired),
				})
				.min(1, t(Resources.Reminders.Validations.nameRequired))
				.max(
					REMINDER_NAME_MAX_LENGTH,
					t(Resources.Reminders.Validations.nameMax),
				),
			date: z.date({
				required_error: t(Resources.Reminders.Validations.dateRequired),
				invalid_type_error: t(Resources.Reminders.Validations.dateRequired),
			}),
			recurrence: z.nativeEnum(RecurrenceEnum, {
				required_error: t(Resources.Form.Validations.required),
			}),
		})
		.superRefine((data, ctx) => {
			const dateChanged =
				!initialDate ||
				startOfDay(data.date).getTime() !== startOfDay(initialDate).getTime();

			if (
				dateChanged &&
				startOfDay(data.date).getTime() <= startOfDay(new Date()).getTime()
			) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					path: ["date"],
					message: t(Resources.Reminders.Validations.dateFuture),
				});
			}
		});

export type ReminderFormModel = z.infer<
	ReturnType<typeof getReminderFormSchema>
>;

export const REMINDER_FORM_DEFAULT_VALUES: DefaultValues<ReminderFormModel> = {
	// The custom typeKey differs per entity (portfolio_custom / property_custom /
	// vehicle_custom), so it is filled in from GET /v2/reminders/types.
	typeKey: "",
	name: "",
	date: undefined,
	recurrence: RecurrenceEnum.Yearly,
};

export const mapReminderToFormModel = (
	reminder: Reminder,
): ReminderFormModel => ({
	typeKey: reminder.typeKey,
	name: reminder.name,
	date: reminder.date,
	recurrence: reminder.recurrence,
});
